import * as React from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Button, CircularProgress, Link, Stack, Typography } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { FilesZipStatus } from "gen/clients/models/FilesZipStatus";
import { useMutation, useQuery } from "react-query";
import { JobRequestsService } from "gen/clients/services/JobRequestsService";

interface Props {
  jobRequestId: string;
  zipFileName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const DownloadSourceFilesDialog: React.FC<Props> = ({ jobRequestId, zipFileName, onClose, onSuccess }) => {
  const [firstQuery, setFirstQuery] = React.useState(true);
  const [shouldQuery, setShouldQuery] = React.useState(true);
  const { data: zipStatus, isLoading } = useQuery(["getSourceFilesZipStatus", jobRequestId], {
    queryFn: () => JobRequestsService.getZipSourceFilesStatus({ jobRequestId }),
    refetchInterval: shouldQuery ? 1000 : false,
    enabled: shouldQuery,
    cacheTime: 0
  });
  const { mutate, error } = useMutation(JobRequestsService.zipJobRequestSourceFiles);

  React.useEffect(() => {
    if (zipStatus) {
      switch (zipStatus.status) {
        case FilesZipStatus.NOT_STARTED:
          setFirstQuery(false);
          mutate({ jobRequestId, zipFileName });
          break;
        case FilesZipStatus.FAILED:
          if (firstQuery) {
            setFirstQuery(false);
            mutate({ jobRequestId, zipFileName });
          } else {
            setShouldQuery(false);
          }
          break;
        case FilesZipStatus.COMPLETED:
          setShouldQuery(false);
          break;
        default:
          break;
      }
    }
  }, [firstQuery, jobRequestId, mutate, zipFileName, zipStatus]);

  const handleLinkClick = React.useCallback(() => {
    onSuccess();
  }, [onSuccess]);

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="sm">
      <DialogTitle>Download All Source Files</DialogTitle>
      <DialogContent>
        <Stack sx={{ py: 5 }}>
          {!!error && <ApiErrorMessage apiError={error} />}
          {(isLoading ||
            (zipStatus?.status &&
              [FilesZipStatus.IN_PROGRESS, FilesZipStatus.NOT_STARTED].includes(zipStatus?.status))) && (
            <Stack direction="row" spacing={1}>
              <CircularProgress size={24} style={{ marginRight: 8 }} />
              <Typography>Preparing a zip file with source files. Please wait.</Typography>
            </Stack>
          )}
          {zipStatus?.status === FilesZipStatus.FAILED && (
            <Typography color="error.main">Oops, something went wrong. Please try again.</Typography>
          )}
          {zipStatus?.status === FilesZipStatus.COMPLETED && (
            <Typography>
              A zip file containing the source files is ready for download.
              <Link
                href={zipStatus.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                sx={{ ml: 1 }}
              >
                Click here
              </Link>
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export { DownloadSourceFilesDialog };
